import { Transition } from '@headlessui/react';
import { Button } from '@mui/material';
import type { NewContractWithoutPlan } from '@vy/types';
import { CONTRACT_CREATION_TYPES, CONTRACT_STATUSES, CONTRACT_TYPES, SYNC_STATUSES } from '@vy/types';
import type { ItemOf } from '@vy/utils';
import { debounce, isNotEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useStepCounter } from '../../../../../hooks/use-step-counter';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { getPrivateApi } from '../../../../../services/api';
import { runMutation } from '../../../../../utils/vy-mutation';
import type { NewPlanAssignment } from '../../plans-page-types/new-plan-assignment-schema';
import type { PlanAssignmentCustomerData } from '../../plans-page-types/plan-assignment-customer-data-schema';
import type { PlanAssignmentInput } from '../../plans-page-types/plan-assignment-input-schema';
import { AssignPlanStepCustomers } from '../AssignPlanStepCustomers';
import { AssignPlanStepStartDate } from '../AssignPlanStepStartDate';
import { STEPS_COUNT } from './create-contract-sidebar-consts';
import { validateCreateContractStep } from './create-contract-step-validator';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { invalidateCustomersQueries } from '../../../../../utils/query-invalidation';

type CreateContractSidebarProps = {
  title: string;
  planId: string;
};

export const CreateContractSidebar: React.FC<CreateContractSidebarProps> = ({ title, planId }) => {
  const queryClient = useQueryClient();
  const { currentStep, incrementSteps, decrementSteps, isLastStep } = useStepCounter(STEPS_COUNT);
  const { watch } = useFormContext<PlanAssignmentInput, NewPlanAssignment>();

  const navigate = useVyNavigate();

  const selectedCustomers = watch('selectedCustomers') ?? [];
  const startDate = watch('contract.startDate');

  const currencyOverride = watch('contract.currencyOverride');
  const contractsToCreate = useMemo(
    () => buildCustomerInputs(planId, startDate, selectedCustomers, currencyOverride),
    [planId, startDate, selectedCustomers, currencyOverride],
  );
  const isStepValid = useMemo(
    () => validateCreateContractStep(currentStep, selectedCustomers, startDate),
    [currentStep, selectedCustomers, startDate],
  );

  const saveContractsMutation = useMutation(saveContracts);

  const onFinish = useCallback(async () => {
    await runMutation(
      saveContractsMutation,
      contractsToCreate,
      {
        pending: 'Creating contracts...',
        success: 'Contracts created',
        error: 'Failed to create contracts',
      },
      {
        onSuccess: async ({ contracts: [createdContract] }) => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.contracts()),
            invalidateCustomersQueries(queryClient),
          ]);
          navigate(`/plans/contracts/${createdContract.id}`, { replace: true });
        },
      },
    );
  }, [saveContractsMutation, contractsToCreate]);

  return (
    <Transition
      show
      enter="transition ease-in-out duration-300 transform"
      enterFrom="-translate-x-full"
      enterTo="translate-x-0"
      leave="transition ease-in-out duration-300 transform"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full"
    >
      <div className="fixed inset-y-0 right-0 z-10 flex h-screen w-[25rem] flex-col items-center bg-white pt-10 shadow-[-17px_4px_24px_0_rgba(0,0,0,0.05)]">
        <div className="flex w-full flex-col gap-4 overflow-y-auto px-8">
          {currentStep === 0 && <AssignPlanStepCustomers title={title} />}
          {currentStep === 1 && <AssignPlanStepStartDate title={title} />}
        </div>
        {isNotEmptyArray(selectedCustomers) && (
          <div className="bg-input mt-auto flex w-full justify-center py-3">
            {isLastStep && (
              <button
                disabled={saveContractsMutation.isLoading}
                type="button"
                className={cn('text-style mr-10 px-5 text-lg font-medium duration-300', {
                  'opacity-0': currentStep === 0,
                })}
                onClick={decrementSteps}
              >
                Back
              </button>
            )}
            <Button
              variant="contained"
              size="large"
              type="button"
              sx={{ columnGap: '12px' }}
              onClick={!isLastStep ? incrementSteps : debounce(onFinish)}
              disabled={!isStepValid}
            >
              {!isLastStep ? 'Next' : 'Finish'}
            </Button>
          </div>
        )}
      </div>
    </Transition>
  );
};

function saveContracts(contracts: NewContractWithoutPlan['contracts']) {
  const privateApi = getPrivateApi();
  const newContracts = buildNewContractWithoutPlan(contracts);

  return privateApi.createContracts({ createContractsRequest: newContracts });
}

function buildNewContractWithoutPlan(contracts: NewContractWithoutPlan['contracts']): NewContractWithoutPlan {
  return {
    contracts,
    creationType: CONTRACT_CREATION_TYPES.CreateContractWithoutPlan,
  };
}

function buildCustomerInputs(
  planId: string,
  startDate: Date | undefined,
  customers: PlanAssignmentCustomerData[],
  currencyOverride: NewContractWithoutPlan['contracts'][number]['currencyOverride'] | undefined,
) {
  const canBuildValidCustomerInputs = isNotEmptyArray(customers) && isNotNullOrUndefined(startDate);

  if (!canBuildValidCustomerInputs) return [];

  return customers.map<ItemOf<NewContractWithoutPlan['contracts']>>(({ id: customerId }) => ({
    planId,
    startDate,
    customerId,
    status: CONTRACT_STATUSES.Active,
    syncStatus: SYNC_STATUSES.None,
    currencyOverride: currencyOverride ?? undefined,
    contractType: CONTRACT_TYPES.Standard,
  }));
}
